import validator from 'email-validator';
import noop from 'lodash/noop';
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Button, VARIATION } from '@zocdoc-frontend-common/components-button';
import Textbox from '@zocdoc-frontend-common/components-textbox';
import {
    withCustomMetrics,
    MetricsClickable,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { red70, slate30 } from '@zocdoc-mezzanine/foundations-colors';
import {
    paragraphMedium,
    labelSmall,
} from '@zocdoc-mezzanine/foundations-typography';
import { SignInWithEmailMetrics, SocialLoginErrorCodes } from './constants';

/**
 *
 * @param {string} className
 * @param {boolean} showFacebookLogin - ab experiment for whether or not we show the facebook login button
 * @param {number} socialLoginValidationError
 * @param {function} onSubmitEmail
 * @param {function} sendMetric
 */
const BaseSignInWithEmail = ({
    className,
    showFacebookLogin,
    socialLoginValidationError,
    onSubmitEmail = noop,
    sendMetric,
}) => {
    const [email, setEmail] = useState('');
    const [shouldValidateEmail, setShouldValidateEmail] = useState(true);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');

    const onSubmit = useCallback(
        (e) => {
            e.preventDefault();

            if (shouldValidateEmail) {
                if (!validator.validate(email)) {
                    setShouldValidateEmail(false);
                    if (email) {
                        // not empty string
                        setEmailErrorMessage('Please type a valid email.');
                    } else {
                        setEmailErrorMessage('Please type an email.');
                    }

                    sendMetric({
                        action: SignInWithEmailMetrics.ViewErrorMessage,
                        element: 'ErrorMessage',
                        initiator: EVENT_INITIATORS.USER,
                        interactionType: INTERACTION_TYPES.VIEW,
                    });
                } else {
                    onSubmitEmail(email);
                }
            }
        },
        [email, onSubmitEmail, sendMetric, shouldValidateEmail]
    );

    useEffect(() => {
        sendMetric({
            element: 'BaseSocialLoginButtons',
            action: SignInWithEmailMetrics.View,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
        });
    }, [sendMetric]);

    const onEmailChange = (e) => {
        setShouldValidateEmail(true);
        setEmailErrorMessage('');
        setEmail(e.target.value);
    };

    const errorMessage =
        emailErrorMessage ||
        getSocialLoginValidationErrorMessage({
            socialLoginValidationError: socialLoginValidationError,
            showFacebookLogin: showFacebookLogin,
        });

    return (
        <div className={className}>
            <EmailHeader>Email Address</EmailHeader>
            <form method="post" onSubmit={(e) => onSubmit(e)}>
                <MetricsClickable
                    action={SignInWithEmailMetrics.ClickTextbox}
                    element="EmailTextbox"
                >
                    <StyledTextbox
                        className="email-form-textbox exclude-from-playback"
                        data-test="email-form-textbox"
                        placeholder={'Email address'}
                        onChange={onEmailChange}
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                    />
                </MetricsClickable>
                <HiddenPasswordInput type="password" name="password" />
                {errorMessage && (
                    <ErrorMessage data-test="email-form-error-message">
                        {errorMessage}
                    </ErrorMessage>
                )}
                <MetricsClickable
                    action={SignInWithEmailMetrics.ClickContinue}
                    element="ContinueButton"
                >
                    <StyledButton
                        data-test="email-form-continue-button"
                        variation={VARIATION.PRIMARY}
                    >
                        Continue
                    </StyledButton>
                </MetricsClickable>
            </form>
        </div>
    );
};

function getSocialLoginValidationErrorMessage({
    socialLoginValidationError,
    showFacebookLogin,
}) {
    if (socialLoginValidationError) {
        switch (socialLoginValidationError) {
            case SocialLoginErrorCodes.SocialLoginOther:
                return 'Something went wrong, please try again...';
            case SocialLoginErrorCodes.SocialLoginRequestExpired:
                return 'Signin timed out, please try again.';
            case SocialLoginErrorCodes.SocialLoginNonPatient:
                return showFacebookLogin
                    ? 'Only patients can log in with Facebook or Google. Please log in with your email instead.'
                    : 'Only patients can log in with Google. Please log in with your email instead.';
            case SocialLoginErrorCodes.SocialLoginCannotBeLinkedForMfaUsers:
                return `We couldn't log you in because you have two-step verification on. Please log in with your password instead.`;
            default:
                return '';
        }
    } else {
        return '';
    }
}

export const SignInWithEmail = withCustomMetrics({
    component: 'SignInWithEmail',
})(BaseSignInWithEmail);

const EmailHeader = styled.h3`
    ${labelSmall};
    margin-bottom: 4px;
    text-align: left;
`;

const StyledTextbox = styled(Textbox)`
    &:focus::placeholder {
        color: ${slate30};
    }
    margin-bottom: 16px;
`;

const HiddenPasswordInput = styled.input`
    display: none;
`;

const StyledButton = styled(Button)`
    font-size: 15px;
    line-height: 18px;
    padding: 11px 0;
    width: 100%;
`;

const ErrorMessage = styled.div`
    ${paragraphMedium};
    color: ${red70};
    margin-bottom: 3px;
    margin-top: -5px;
    text-align: left;
`;
